import { db } from './db';
import { эпоха } from './epoch';
import {
  довестиОткрытие, имяОтказа, отказСоединения, попытатьсяПродвинутьПоколение, текущееПоколение,
} from './connectionGuard';
import { report, отпустить, следПереоткрытия } from '../sentry';

/** Классификатор (`отказСоединения`) и счётчик поколений
 *  (`текущееПоколение`) живут в `./connectionGuard` — модуле БЕЗ
 *  зависимости от `./db`, — а не здесь. Причина не стилистическая: этот
 *  файл импортирует `db` (нужен для `db.close()` в `переоткрытьСоединение`
 *  ниже), а центральный слой Dexie (DBCore middleware, `db.ts`)
 *  регистрируется ВНУТРИ конструктора `TrainerDb`, до первого открытия
 *  базы, и ему тоже нужны классификатор со счётчиком. Если бы они лежали
 *  здесь, `db.ts` пришлось бы импортировать ИХ ОТСЮДА — а этот файл
 *  импортирует `db` ИЗ `db.ts`. Цикл: `db.ts` → `dbReconnect.ts` → `db.ts`,
 *  и `export const db = new TrainerDb()` в момент, когда `db.ts` доходит до
 *  своего импорта `dbReconnect.ts`, ещё не исполнен — импортирующий получил
 *  бы `undefined` вместо базы (временная мёртвая зона `const` в цикле
 *  ES-модулей). `connectionGuard.ts` не знает о `db` вовсе, и цикла нет.
 *
 *  Реэкспортированы без изменения сигнатур: весь остальной код (`sync.ts`,
 *  тесты) как звал `отказСоединения`/`текущееПоколение` из этого файла, так
 *  и продолжает. */
export { отказСоединения, текущееПоколение };

/** Сколько всего попыток внутри `сПовторомПриОбрыве`, включая первую.
 *
 *  Было 2 (первая попытка плюс один повтор). Мало: второе, ЧУЖОЕ,
 *  переоткрытие соединения, случившееся между снятием поколения для повтора
 *  и его собственным отказом, оставляло вызывающего с уже ВОССТАНОВЛЕННЫМ
 *  соединением и только что провалившимся единственным повтором — третьей
 *  попытки, которая прошла бы по этому здоровому соединению, уже не было.
 *  Три попытки — по одному снимку поколения на каждую — переживают такую
 *  гонку. */
const МАКС_ПОПЫТОК = 3;

/** Прогнать `action`, и если Dexie сообщает об оборванном соединении —
 *  закрыть и заново открыть базу и повторить `action`, пока не кончатся
 *  попытки (`МАКС_ПОПЫТОК`) или пока `action` не отдаст успех либо отказ,
 *  не являющийся обрывом соединения.
 *
 *  Поколение снимается ДО КАЖДОЙ попытки, а не один раз в начале: к моменту
 *  отказа конкретной попытки кто-то другой (параллельный вызов, сам
 *  центральный слой Dexie из `db.ts`) уже мог переоткрыть то же самое
 *  соединение, и снятое здесь значение — то, что было ДЕЙСТВУЮЩИМ, когда
 *  началась именно эта попытка (`попытатьсяПродвинутьПоколение` в
 *  `переоткрытьСоединение` не даст закрыть соединение второй раз, если это
 *  так).
 *
 *  Эпоха выхода (`эпоха()` из `./epoch`) снимается один раз, ДО первой
 *  попытки. Стирание устройства поднимает её, и повтор ПОСЛЕ стирания писал
 *  бы в стёртое устройство — то есть ровно в то, ради чего стирание вообще
 *  случилось. Эпоха, сменившаяся между попытками, останавливает повтор:
 *  отказ пробрасывается вызывающему как есть, без дальнейших попыток и без
 *  переоткрытия.
 *
 *  Отчёт в Sentry — НЕ забота этой обёртки, а вызывающего места, которое и
 *  раньше звало `report` на отказе. Если один из повторов восстановил
 *  соединение — вызывающий видит успех и не зовёт `report` вовсе. Если все
 *  попытки исчерпаны — вызывающий получает ПОСЛЕДНИЙ отказ и зовёт `report`
 *  ровно там же и ровно один раз, где звал бы его и без этой обёртки.
 *
 *  Годится только для действий, безопасных к ПОЛНОМУ повтору: чтений и
 *  записи по заранее известному ключу (`put`/`bulkPut`, транзакция Dexie
 *  атомарна и при отказе не оставляет частичной записи). Действие с
 *  побочным эффектом, для которого повтор небезопасен (сетевой запрос,
 *  вставка с автоинкрементом), сюда заворачивать нельзя. */
export async function сПовторомПриОбрыве<T>(action: () => Promise<T>): Promise<T> {
  const эпохаНачала = эпоха();
  for (let попытка = 1; ; попытка += 1) {
    const снятое = текущееПоколение();
    try {
      return await action();
    } catch (error) {
      if (!отказСоединения(error)) throw error;
      if (попытка >= МАКС_ПОПЫТОК) throw error;
      /** Стирание устройства случилось МЕЖДУ попытками — повтор писал бы в
       *  уже стёртые таблицы. Отказ этой попытки пробрасывается как есть, а
       *  не отказ какого-то нового рода: вызывающий видит тот же самый
       *  обрыв соединения, каким он и был бы без этой проверки, — разница
       *  только в том, что дальше не повторяем. */
      if (эпоха() !== эпохаНачала) throw error;
      переоткрытьСоединение(снятое, error);
    }
  }
}

/** Закрыть и заново открыть базу Dexie после оборванного соединения.
 *
 *  `снятое` не передан — переоткрыть безусловно, как раньше. `снятое`
 *  передан и НЕ совпадает с текущим поколением — значит кто-то другой уже
 *  переоткрыл соединение с тех пор, как вызывающий снял своё поколение
 *  (`попытатьсяПродвинутьПоколение` в `connectionGuard.ts`), и закрывать
 *  снова НЕЛЬЗЯ: это убило бы чужие транзакции из очереди, вставшие в неё
 *  уже на СВЕЖЕМ соединении. Такой вызов тихо ничего не делает — новое
 *  соединение уже есть, повтор вызывающего действия найдёт его сам.
 *
 *  `disableAutoOpen: false` — единственный смысл этой строки, как и прежде.
 *  Обычный `db.close()` НАВСЕГДА выключает автооткрытие, и следующий же
 *  запрос упал бы `DatabaseClosedError` вместо того, чтобы тихо переоткрыть
 *  базу. Само переоткрытие ленивое — Dexie откроет базу заново при первом
 *  следующем запросе, — но полагаться только на него нельзя (клетки B и C
 *  пробного файла ревью: переоткрытие может зависнуть или провалиться, а
 *  без обращения к базе никто этого не заметит). Поэтому следом запускается
 *  `довестиОткрытие` — само доведение идёт в ФОНЕ, через `отпустить`: эта
 *  функция синхронная, её зовут из обработчика отказа, и ждать открытие
 *  здесь было бы нечем и незачем.
 *
 *  `причина` — исходная ошибка (для следа в Sentry, `следПереоткрытия`);
 *  без неё (ручной вызов в тестах) след ляжет как `'неизвестно'`. */
export function переоткрытьСоединение(снятое?: number, причина?: unknown): void {
  if (попытатьсяПродвинутьПоколение(снятое)) {
    следПереоткрытия(имяОтказа(причина));
    db.close({ disableAutoOpen: false });
    отпустить(довестиОткрытие(db), 'переоткрытие');
  }
}

/** Центральная проверка соединения — зовётся при возврате вкладки из фона,
 *  ДО того, как экран вообще успеет что-нибудь запросить.
 *
 *  Три чтения, а не одно, и это не перестраховка: `db.device.get(...)` —
 *  точечный запрос, `db.profiles.limit(1).toArray()` — `getAll`/курсор БЕЗ
 *  реверса, а `db.sessions.orderBy('[profileId+startedAt]').reverse()` —
 *  курсор по составному индексу В ОБРАТНОМ порядке, ту же форму, что
 *  `loadSessions` в `repository.ts` (там и упал настоящий обрыв, Sentry
 *  MULT-TRAINER-9): `reverse()` не даёт Dexie срезать путь через `getAll`
 *  (у него нет направления), и WebKit роняет обрыв на этой дороге отдельно
 *  от первых двух. Профиль не указан намеренно — это проверка ЗДОРОВЬЯ
 *  устройства, а не конкретного ребёнка. Проверка любым ОДНИМ из трёх ловит
 *  только часть дороги.
 *
 *  Отказ соединения — переоткрыть и повторить ВСЕ ТРИ чтения ЕЩЁ РАЗ, а не
 *  просто выйти. Без повторной проверки о базе, мёртвой И ПОСЛЕ
 *  переоткрытия (диск сломан, IndexedDB заблокирован политикой организации
 *  посреди сессии), мы не узнавали бы никогда: `сПовторомПриОбрыве` в
 *  других местах в этом случае просто перебрасывает второй отказ
 *  вызывающему, а здесь вызывающего нет — событие обязано уйти в Sentry
 *  ЗДЕСЬ, иначе нигде. Отказ повторной проверки — снова обрыв соединения —
 *  уходит в `report` под ОТДЕЛЬНОЙ меткой (`:после-переоткрытия`): по ней
 *  видно в Sentry, что дело не в обычном временном обрыве, а в том, что
 *  переоткрытие не помогло.
 *
 *  Посторонний отказ (не обрыв соединения) — сразу в `report`, без всякого
 *  повтора: это не то, что чинит переоткрытие соединения.
 *
 *  Функция НИКОГДА не бросает: она вызвана из обработчика события, а не из
 *  цепочки, которую кто-то ждёт и умеет разобрать по отказу. */
export async function проверитьСоединение(): Promise<void> {
  const снятое = текущееПоколение();
  try {
    await db.device.get('singleton');
    await db.profiles.limit(1).toArray();
    await db.sessions.orderBy('[profileId+startedAt]').reverse().limit(1).toArray();
  } catch (error) {
    if (!отказСоединения(error)) {
      report(error, 'проверкаСоединения');
      return;
    }
    переоткрытьСоединение(снятое, error);
    try {
      await db.device.get('singleton');
      await db.profiles.limit(1).toArray();
      await db.sessions.orderBy('[profileId+startedAt]').reverse().limit(1).toArray();
    } catch (повторнаяОшибка) {
      report(
        повторнаяОшибка,
        отказСоединения(повторнаяОшибка) ? 'проверкаСоединения:после-переоткрытия' : 'проверкаСоединения',
      );
    }
  }
}

/** Подписка на возврат вкладки из фона — тремя разными дорогами сразу.
 *
 *  `visibilitychange` в `visible` — обычный возврат из фона. `pageshow` с
 *  `persisted: true` — восстановление страницы из bfcache (Safari отдаёт её
 *  из кеша без перезагрузки, `visibilitychange` при этом не гарантирован —
 *  Page Lifecycle API, раздел о bfcache). `resume` — событие той же Page
 *  Lifecycle API для случаев, которые не покрывает ни одно из двух первых
 *  (нативные обёртки, отдельные версии Safari). Три подписки, а не одна: ни
 *  одна из них не покрывает всех путей возврата в одиночку, и обрыв,
 *  случившийся на пути, который не покрыт, не будет починен до первого
 *  запроса экрана — то есть ровно тем случайным путём, ради ухода от
 *  которого весь этот файл существует.
 *
 *  Уходу в фон (и восстановлению страницы БЕЗ признака `persisted`) здесь
 *  нечего чинить — обрываться в этот момент ещё нечему.
 *
 *  `отпустить`, а не голый вызов без разбора отказа: `проверитьСоединение`
 *  не бросает по построению, но тихая помощница здесь была бы шагом назад
 *  (см. `отпустить` в `src/sentry.ts`) — если это построение однажды
 *  перестанет быть верным, отказ обязан остаться слышным, а не потеряться
 *  необработанным отклонением обещания. */
export function следитьЗаВозвращением(): () => void {
  const onVisible = (): void => {
    if (document.visibilityState !== 'visible') return;
    отпустить(проверитьСоединение(), 'проверкаСоединения');
  };
  const onPageShow = (event: Event): void => {
    if (!(event as PageTransitionEvent).persisted) return;
    отпустить(проверитьСоединение(), 'проверкаСоединения');
  };
  const onResume = (): void => {
    отпустить(проверитьСоединение(), 'проверкаСоединения');
  };
  document.addEventListener('visibilitychange', onVisible);
  document.addEventListener('pageshow', onPageShow);
  document.addEventListener('resume', onResume);
  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    document.removeEventListener('pageshow', onPageShow);
    document.removeEventListener('resume', onResume);
  };
}
